import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ActionPlanTasksSchema } from '../../../definitions/schemas';
import { ellipsis } from '../../../theme/styles/mixins';
import { DRAWER_Z_INDEX } from '../../../definitions/constants';
import { isTaskCompleted } from './helper';
import { ActionPlanTaskStatus } from '../../../enums/ActionPlans';
import { pluralizeLabel } from '../../../lib/Language';
import { useI18n } from '../../../lib/hook/useI18n';

interface Props {
  onClose: () => void;
  onComplete: (
    completePendingSubtasks: boolean,
    subtasks: ActionPlanTasksSchema
  ) => void;
  subtasks: ActionPlanTasksSchema;
}

const CompleteTaskConfirmDialog: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const { t } = useI18n();

  const { onClose, onComplete, subtasks } = props;

  const pendingSubtasks = useMemo(
    () => subtasks.filter((subtask) => !isTaskCompleted(subtask)),
    [subtasks]
  );

  const subtasksNumber = pendingSubtasks.length;
  const pluralizedLabel = pluralizeLabel(subtasksNumber, 'map.subtask');

  const handleComplete = (completePendingSubtasks: boolean) => {
    onComplete(
      completePendingSubtasks,
      subtasks.map((subtask) => ({
        ...subtask,
        status: completePendingSubtasks
          ? ActionPlanTaskStatus.Completed
          : subtask.status,
      }))
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open
      sx={{ zIndex: DRAWER_Z_INDEX + 1 }}
    >
      <DialogTitle>{t('map.all_subtask.modal.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('map.all_subtask.modal.body', {
            number: subtasksNumber,
            type: pluralizedLabel,
          })}
        </DialogContentText>
        <Box
          sx={(theme) => ({
            maxHeight: theme.spacing(22),
            overflow: 'auto',
          })}
        >
          <Box component="ul" pl={4}>
            {pendingSubtasks.map((subtask) => (
              <Box
                component="li"
                key={`subtask-confirm-status-${subtask.id}`}
                my={2}
                title={subtask.name}
              >
                <Typography
                  color="textPrimary"
                  sx={{ ...ellipsis() }}
                  variant="body2"
                >
                  {subtask.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box flex="1">
          <Button color="inherit" onClick={onClose} variant="text">
            {t('map.all_subtask.modal.cancel')}
          </Button>
        </Box>
        <Button
          color="inherit"
          onClick={() => handleComplete(false)}
          variant="text"
        >
          {t('map.all_subtask.modal.no')}
        </Button>
        <Button
          color="secondary"
          onClick={() => handleComplete(true)}
          variant="contained"
        >
          {t('map.all_subtask.modal.yes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteTaskConfirmDialog;
